import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faX, faSearch, faCheck, faBolt, faStore } from '@fortawesome/free-solid-svg-icons';
import { decryptData } from '../../utils/storageCrypto';
import { Msg500 } from '../Messages/Messages';

// ---------------------------------------------------------------------------
// Modal: BulkAssignProductsToHubsModal
// Purpose: Select many products at once (optionally filter by category) and
// assign all of them to every hub passed in via `hubs`, in a single request.
// A "bulk apply" row lets the admin set one price/stock for every selected
// product instead of editing each row individually.
//
// Backend endpoints assumed (adjust to match actual API contracts):
//   GET  product-category/get-all-productCategories
//   GET  product/get-all-products?product_category_id=
//   POST hub-product/bulk-assign
//     body: { hub_ids: [...], products: [{ product_id, price, stock, sku, is_active }] }
// ---------------------------------------------------------------------------

const BulkAssignProductsToHubsModal = ({ hubs, onClose, onAssignmentComplete }) => {
  const fetchUrl = process.env.REACT_APP_FORE_BRICKBUCKET;
  const imageUrl = process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || '';

  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [products, setProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState({}); // { productId: { price, stock, sku, is_active } }
  const [selectAll, setSelectAll] = useState(false);
  const [bulkPrice, setBulkPrice] = useState('');
  const [bulkStock, setBulkStock] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const getAuthHeaders = () => {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
    return { 'Content-Type': 'application/json', 'x-token': token };
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts(selectedCategoryId);
  }, [selectedCategoryId]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${fetchUrl}product-category/get-all-productCategories`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (response.ok) setCategories(data.data || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchProducts = async (categoryId) => {
    setIsLoadingProducts(true);
    try {
      const url = categoryId
        ? `${fetchUrl}product/get-all-products?product_category_id=${categoryId}`
        : `${fetchUrl}product/get-all-products`;
      const response = await fetch(url, { method: 'GET', headers: getAuthHeaders() });
      const data = await response.json();
      if (response.ok) {
        setProducts(data.data || []);
      } else {
        setError(data.message || 'Failed to load products.');
      }
    } catch (err) {
      console.error('Failed to fetch products:', err);
      setError('Failed to load products.');
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const filteredProducts = useMemo(() => {
    const term = searchTerm.toLowerCase();
    if (!term) return products;
    return products.filter(p =>
      p.product_name?.toLowerCase().includes(term) || p.SKU?.toLowerCase().includes(term)
    );
  }, [products, searchTerm]);

  const selectedCount = Object.values(selected).filter(v => v?.selected).length;

  const toggleProduct = (product) => {
    setSelected(prev => ({
      ...prev,
      [product._id]: prev[product._id]?.selected
        ? { ...prev[product._id], selected: false }
        : {
            selected: true,
            price: bulkPrice || product.selling_price || 0,
            stock: bulkStock || '',
            sku: product.SKU || '',
            is_active: true,
          }
    }));
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelected({});
    } else {
      const next = {};
      filteredProducts.forEach(p => {
        next[p._id] = {
          selected: true,
          price: bulkPrice || p.selling_price || 0,
          stock: bulkStock || '',
          sku: p.SKU || '',
          is_active: true,
        };
      });
      setSelected(next);
    }
    setSelectAll(!selectAll);
  };

  const updateField = (productId, field, value) => {
    setSelected(prev => ({
      ...prev,
      [productId]: { ...prev[productId], [field]: value }
    }));
  };

  const applyBulkValues = () => {
    setSelected(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(productId => {
        if (!next[productId]?.selected) return;
        next[productId] = {
          ...next[productId],
          ...(bulkPrice !== '' ? { price: parseFloat(bulkPrice) || 0 } : {}),
          ...(bulkStock !== '' ? { stock: parseInt(bulkStock) || 0 } : {}),
        };
      });
      return next;
    });
  };

  const handleAssign = async () => {
    if (selectedCount === 0 || hubs.length === 0) return;
    setIsSaving(true);
    setError('');

    const assignedProducts = Object.entries(selected)
      .filter(([_, data]) => data.selected)
      .map(([productId, data]) => ({
        product_id: productId,
        price: parseFloat(data.price) || 0,
        stock: data.stock !== '' ? parseInt(data.stock) || 0 : null,
        sku: data.sku || '',
        is_active: data.is_active !== false,
      }));

    try {
      const response = await fetch(`${fetchUrl}hub-product/bulk-assign`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          hub_ids: hubs.map(h => h._id),
          products: assignedProducts,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        onAssignmentComplete({ hubCount: hubs.length, productCount: assignedProducts.length });
      } else {
        setError(data.message || 'Failed to assign products.');
      }
    } catch (err) {
      console.error('Bulk assign failed:', err);
      setError('Something went wrong while assigning products. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      {error && <Msg500 message={error} closePopup={() => setError('')} />}
      <div className="bg-white rounded-lg shadow-lg w-full max-w-7xl h-[95vh] sm:h-[90vh] flex flex-col">

        {/* Header */}
        <div className="border-b px-3 sm:px-6 py-3 sm:py-4 flex justify-between items-start sm:items-center">
          <div className="flex-1 min-w-0 pr-3">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              Assign Products to Hubs
            </h2>
            <div className="text-xs sm:text-sm text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
              <FontAwesomeIcon icon={faStore} className="text-gray-400" />
              <span>
                Assigning to <span className="font-medium">{hubs.length}</span> hub{hubs.length !== 1 ? 's' : ''}:
              </span>
              <span className="font-medium truncate max-w-md">
                {hubs.map(h => h.hub_name).join(', ')}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0">
            <FontAwesomeIcon icon={faX} className="text-base sm:text-lg" />
          </button>
        </div>

        {/* Controls */}
        <div className="border-b p-3 sm:p-4 bg-gray-50 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={selectedCategoryId}
              onChange={(e) => setSelectedCategoryId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat._id} value={cat._id}>{cat.product_category_name}</option>
              ))}
            </select>

            <div className="relative flex-1">
              <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                placeholder="Search products by name or SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <button
              onClick={handleSelectAll}
              className="flex items-center justify-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-white whitespace-nowrap bg-white"
            >
              <input type="checkbox" checked={selectAll} readOnly className="rounded" />
              Select All ({filteredProducts.length})
            </button>
          </div>

          {/* Bulk apply row */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 bg-white border border-dashed border-gray-300 rounded-md p-2 sm:p-3">
            <span className="text-xs sm:text-sm text-gray-600 flex items-center gap-1 whitespace-nowrap">
              <FontAwesomeIcon icon={faBolt} className="text-[#F7525A]" /> Bulk apply to selected:
            </span>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">₹</span>
              <input
                type="number"
                placeholder="Price"
                value={bulkPrice}
                onChange={(e) => setBulkPrice(e.target.value)}
                className="pl-8 pr-3 py-2 w-32 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <input
              type="number"
              placeholder="Stock qty"
              value={bulkStock}
              onChange={(e) => setBulkStock(e.target.value)}
              className="px-3 py-2 w-32 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={applyBulkValues}
              disabled={selectedCount === 0}
              className={`px-3 py-2 rounded-md text-sm whitespace-nowrap ${
                selectedCount > 0
                  ? 'bg-[#408dfb] text-white hover:bg-[#3579d9]'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              Apply to {selectedCount || 0} selected
            </button>
          </div>

          <div className="text-xs sm:text-sm text-gray-600 text-right">
            {selectedCount} of {filteredProducts.length} products selected
          </div>
        </div>

        {/* Product list */}
        <div className="flex-1 overflow-auto">
          {isLoadingProducts ? (
            <div className="flex items-center justify-center h-64 text-gray-500 text-sm">Loading products...</div>
          ) : filteredProducts.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {filteredProducts.map(product => {
                const data = selected[product._id] || {};
                const isSelected = !!data.selected;
                return (
                  <div key={product._id} className={`p-3 sm:p-4 ${isSelected ? 'bg-blue-50' : 'bg-white'}`}>
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="flex items-center pt-2 flex-shrink-0">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleProduct(product)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </div>

                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                        {product.product_image ? (
                          <img
                            src={`${imageUrl}${product.product_image}`}
                            alt={product.product_name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        ) : (
                          <span className="text-gray-400 text-xs text-center">No Image</span>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 text-sm sm:text-base truncate">
                              {product.product_name}
                            </h4>
                            <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-1 text-xs sm:text-sm text-gray-500">
                              {product.SKU && <span>SKU: {product.SKU}</span>}
                              {product.uom?.UOM && <span>UOM: {product.uom.UOM}</span>}
                              {product.selling_price !== undefined && <span>Catalog price: ₹{product.selling_price}</span>}
                            </div>
                          </div>
                        </div>

                        {isSelected && (
                          <div className="mt-3 p-3 bg-white rounded-lg border grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Hub Price *</label>
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs">₹</span>
                                <input
                                  type="number"
                                  value={data.price ?? ''}
                                  onChange={(e) => updateField(product._id, 'price', e.target.value)}
                                  className="pl-6 pr-2 py-1.5 w-full border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Stock Qty</label>
                              <input
                                type="number"
                                value={data.stock ?? ''}
                                onChange={(e) => updateField(product._id, 'stock', e.target.value)}
                                className="px-2 py-1.5 w-full border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                placeholder="Optional"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Hub SKU</label>
                              <input
                                type="text"
                                value={data.sku ?? ''}
                                onChange={(e) => updateField(product._id, 'sku', e.target.value)}
                                className="px-2 py-1.5 w-full border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                            <div className="flex items-end">
                              <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  checked={data.is_active !== false}
                                  onChange={(e) => updateField(product._id, 'is_active', e.target.checked)}
                                  className="rounded"
                                />
                                Active at hub
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="text-center px-4">
                <FontAwesomeIcon icon={faSearch} className="text-gray-400 text-3xl sm:text-4xl mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No products found</h3>
                <p className="text-sm sm:text-base text-gray-600">Try a different search term or category.</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-3 sm:p-4 bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
            <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
              {selectedCount} product{selectedCount !== 1 ? 's' : ''} &rarr; {hubs.length} hub{hubs.length !== 1 ? 's' : ''}
            </div>
            <div className="flex gap-2 sm:gap-3">
              <button
                onClick={onClose}
                className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={selectedCount === 0 || isSaving}
                className={`flex-1 sm:flex-initial px-3 sm:px-4 py-2 text-sm rounded-md flex items-center justify-center gap-2 ${
                  selectedCount > 0 && !isSaving
                    ? 'bg-[#f7525a] text-white hover:bg-[#e04850]'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Assigning...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheck} />
                    Assign ({selectedCount})
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkAssignProductsToHubsModal;
